/**
 * /cap-table — Cap table + option pool management.
 *
 * Tabs: Shareholders | Options | Ledger
 *
 * API:
 *   GET  /api/cap-table/summary
 *   GET  /api/cap-table/transactions
 *   GET  /api/cap-table/instruments
 *   POST /api/cap-table/transactions
 *   GET  /api/cap-table/option-grants
 *   POST /api/cap-table/option-grants
 *   GET  /api/cap-table/option-pool
 *   GET  /api/cap-table/equity-ledger
 *   GET  /api/cap-table/equity-summary
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@mui/material";
import { MoneyField } from "@/components/MoneyField";
import { api, getToken } from "@/lib/api";
import { useToast } from "@/components/Toaster";

export const Route = createFileRoute("/_auth/cap-table")({
  component: CapTablePage,
});

// ─── Types ────────────────────────────────────────────────────────────────────

type OwnershipRow = {
  shareholder_id: string;
  shareholder_name: string;
  shareholder_type: string;
  instrument_name: string;
  instrument_type: string;
  total_amount_cents: number;
  total_shares: number;
  ownership_pct: number;
};

type Transaction = {
  id: string;
  transaction_date: string;
  transaction_type: string;
  shareholder_name: string;
  instrument_name: string;
  instrument_type: string;
  amount_cents: number;
  shares: number | null;
  journal_id: string | null;
  memo: string | null;
};

type Instrument = {
  id: string;
  name: string;
  instrument_type: string;
};

type OptionGrant = {
  grant_id: string;
  shareholder_name: string;
  instrument_name: string;
  shares_granted: number;
  shares_vested: number;
  shares_exercised: number;
  shares_outstanding: number;
  pct_vested: number;
  strike_price_cents: number;
  grant_date: string;
  vesting_start_date: string;
  cliff_months: number;
  vest_total_months: number;
  status: string;
  expiry_date: string | null;
  memo: string | null;
  signing_status: string | null;
  signet_envelope_id: string | null;
};

type OptionPool = {
  total_authorized: number;
  total_granted: number;
  total_available: number;
};

type EquityLedgerEntry = {
  id: string;
  entry_date: string;
  event_type: string;
  instrument_id: string;
  instrument_name: string | null;
  from_party_id: string | null;
  from_party_name: string | null;
  to_party_id: string | null;
  to_party_name: string | null;
  shares: number;
  price_per_share_cents: number | null;
  memo: string | null;
  running_shares: number;
};

type EquityInstrumentSummary = {
  instrument_id: string;
  instrument_name: string;
  instrument_type: string;
  authorized: number;
  issued: number;
  options_granted: number;
  options_vested: number;
  options_exercised: number;
  outstanding: number;
  fully_diluted: number;
};

type EquitySummary = {
  by_instrument: EquityInstrumentSummary[];
  totals: {
    authorized: number;
    issued: number;
    options_outstanding: number;
    fully_diluted: number;
  };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const usd = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(cents / 100);

const usdExact = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(cents / 100);

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

const INSTRUMENT_LABELS: Record<string, string> = {
  equity: "Equity", safe: "SAFE", convertible_note: "Conv. Note", option_pool: "Options",
};
const SHAREHOLDER_LABELS: Record<string, string> = {
  founder: "Founder", angel: "Angel", employee: "Employee", advisor: "Advisor", other: "Other",
};
const TXN_LABELS: Record<string, string> = {
  investment: "Investment", note_issued: "Note issued", option_grant: "Option grant",
  conversion: "Conversion", repurchase: "Repurchase", transfer: "Transfer",
};
const INSTRUMENT_COLORS: Record<string, string> = {
  equity:           "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  safe:             "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  convertible_note: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  option_pool:      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};
const SHAREHOLDER_COLORS: Record<string, string> = {
  founder: "bg-slate-100 text-slate-700", angel: "bg-orange-100 text-orange-700",
  employee: "bg-indigo-100 text-indigo-700", advisor: "bg-teal-100 text-teal-700",
  other: "bg-slate-100 text-slate-500",
};
const STATUS_COLORS: Record<string, string> = {
  active:    "bg-green-100 text-green-700",
  exercised: "bg-blue-100 text-blue-700",
  cancelled: "bg-red-100 text-red-600",
  expired:   "bg-slate-100 text-slate-500",
};

const SIGNING_LABELS: Record<string, string> = {
  pending:    "Awaiting signature",
  in_progress:"In progress",
  completed:  "Signed",
  voided:     "Voided",
  declined:   "Declined",
  no_signet:  "PDF ready",
};
const SIGNING_COLORS: Record<string, string> = {
  pending:    "bg-amber-100 text-amber-700",
  in_progress:"bg-blue-100 text-blue-700",
  completed:  "bg-green-100 text-green-700",
  voided:     "bg-slate-100 text-slate-500",
  declined:   "bg-red-100 text-red-500",
  no_signet:  "bg-slate-100 text-slate-600",
};

const EVENT_LABELS: Record<string, string> = {
  authorized:       "Authorized",
  issued:           "Issued",
  option_granted:   "Option granted",
  option_vested:    "Option vested",
  option_exercised: "Exercised",
  option_cancelled: "Cancelled",
  option_expired:   "Expired",
  transferred:      "Transfer",
  converted:        "Converted",
  repurchased:      "Repurchased",
};

const EVENT_COLORS: Record<string, string> = {
  authorized:       "bg-slate-100 text-slate-600",
  issued:           "bg-emerald-100 text-emerald-700",
  option_granted:   "bg-purple-100 text-purple-700",
  option_vested:    "bg-indigo-100 text-indigo-700",
  option_exercised: "bg-blue-100 text-blue-700",
  option_cancelled: "bg-red-100 text-red-500",
  option_expired:   "bg-slate-100 text-slate-400",
  transferred:      "bg-amber-100 text-amber-700",
  converted:        "bg-cyan-100 text-cyan-700",
  repurchased:      "bg-rose-100 text-rose-700",
};

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

// ─── Ownership bar ────────────────────────────────────────────────────────────

const DONUT_COLORS = ["#3b82f6","#10b981","#f59e0b","#8b5cf6","#ef4444","#06b6d4","#f97316","#6366f1"];

function OwnershipBar({ rows }: { rows: OwnershipRow[] }) {
  if (rows.length === 0) return null;
  const byHolder: Record<string, number> = {};
  for (const r of rows) byHolder[r.shareholder_name] = (byHolder[r.shareholder_name] ?? 0) + r.ownership_pct;
  const entries = Object.entries(byHolder).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-2">
      <div className="flex h-4 rounded-full overflow-hidden w-full">
        {entries.map(([name, p], i) => (
          <div key={name} style={{ width: `${p}%`, backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} title={`${name}: ${p.toFixed(1)}%`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {entries.map(([name, p], i) => (
          <div key={name} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            {name} · {p.toFixed(1)}%
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Vesting progress bar ─────────────────────────────────────────────────────

function VestingBar({ grant }: { grant: OptionGrant }) {
  const vestedPct = grant.shares_granted > 0 ? (grant.shares_vested / grant.shares_granted) * 100 : 0;
  const exercisedPct = grant.shares_granted > 0 ? (grant.shares_exercised / grant.shares_granted) * 100 : 0;
  return (
    <div className="w-32">
      <div className="flex h-2 rounded-full overflow-hidden bg-slate-100 dark:bg-gray-700">
        {/* exercised = solid blue */}
        <div className="bg-blue-500" style={{ width: `${exercisedPct}%` }} />
        {/* vested but unexercised = lighter blue */}
        <div className="bg-blue-200 dark:bg-blue-800" style={{ width: `${Math.max(0, vestedPct - exercisedPct)}%` }} />
      </div>
      <p className="text-xs text-slate-400 mt-0.5 tabular-nums">{vestedPct.toFixed(0)}% vested</p>
    </div>
  );
}

// ─── Add Investment dialog ────────────────────────────────────────────────────

const EMPTY_INV = {
  shareholder_name: "", shareholder_type: "angel", instrument_type: "safe",
  instrument_name: "", amount_str: "",
  transaction_date: new Date().toISOString().slice(0, 10), memo: "",
};

function AddInvestmentDialog({ open, onClose, instruments }: { open: boolean; onClose: () => void; instruments: Instrument[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_INV);

  const mutation = useMutation({
    mutationFn: async () => {
      const amount_cents = Math.round(parseFloat(form.amount_str) * 100);
      if (isNaN(amount_cents) || amount_cents <= 0) throw new Error("Enter a valid amount.");
      const name = form.instrument_name.trim() ||
        ({ equity:"Common", safe:"SAFE", convertible_note:"Convertible Note", option_pool:"Option Pool" }[form.instrument_type] ?? form.instrument_type);
      return api("/api/cap-table/transactions", { method: "POST", body: {
        shareholder_name: form.shareholder_name.trim(), instrument_name: name,
        instrument_type: form.instrument_type, amount_cents,
        transaction_date: form.transaction_date, gl_cash_account_code: "1110",
        memo: form.memo || null,
      }});
    },
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["cap-table"] });
      toast.success(`Recorded${r.journal_no ? ` · Journal ${r.journal_no}` : ""}${r.warning ? ` — ${r.warning}` : ""}`);
      setForm(EMPTY_INV); onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const set = (k: keyof typeof EMPTY_INV) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));
  const existingForType = instruments.filter(i => i.instrument_type === form.instrument_type).map(i => i.name);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record investment</DialogTitle>
      <DialogContent>
        <div className="space-y-3 pt-2">
          <TextField label="Investor name" value={form.shareholder_name} onChange={set("shareholder_name")} fullWidth size="small" required placeholder="e.g. Jane Smith" />
          <TextField select label="Investor type" value={form.shareholder_type} onChange={set("shareholder_type")} fullWidth size="small">
            {Object.entries(SHAREHOLDER_LABELS).map(([v,l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
          <TextField select label="Instrument" value={form.instrument_type} onChange={set("instrument_type")} fullWidth size="small">
            <MenuItem value="equity">Equity (Common / Preferred)</MenuItem>
            <MenuItem value="safe">SAFE</MenuItem>
            <MenuItem value="convertible_note">Convertible Note</MenuItem>
          </TextField>
          <TextField label="Instrument name" value={form.instrument_name} onChange={set("instrument_name")} fullWidth size="small"
            placeholder={{ equity:"Common", safe:"SAFE", convertible_note:"Convertible Note" }[form.instrument_type] ?? ""}
            helperText={existingForType.length ? `Existing: ${existingForType.join(", ")}` : "Leave blank for default"} />
          <MoneyField label="Amount" currency="USD" value={form.amount_str} onChange={(v) => setForm(f => ({ ...f, amount_str: v }))} fullWidth size="small" required placeholder="100000" />
          <TextField label="Date" value={form.transaction_date} onChange={set("transaction_date")} fullWidth size="small" required type="date" InputLabelProps={{ shrink: true }} />
          <TextField label="Memo (optional)" value={form.memo} onChange={set("memo")} fullWidth size="small" multiline rows={2} />
        </div>
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-slate-50">Cancel</button>
        <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending || !form.shareholder_name.trim() || !form.amount_str}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
          {mutation.isPending ? "Saving…" : "Record investment"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Grant Options dialog ─────────────────────────────────────────────────────

const EMPTY_GRANT = {
  employee_name: "", shares_str: "", strike_str: "",
  grant_date: new Date().toISOString().slice(0, 10),
  cliff_months: "12", vest_total_months: "48", vesting_schedule: "monthly", memo: "",
};

function GrantOptionsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_GRANT);

  const mutation = useMutation({
    mutationFn: async () => {
      const shares_granted = parseInt(form.shares_str, 10);
      const strike_price_dollars = parseFloat(form.strike_str);
      if (!shares_granted || shares_granted <= 0) throw new Error("Enter a valid share count.");
      if (isNaN(strike_price_dollars) || strike_price_dollars < 0) throw new Error("Enter a valid strike price.");
      return api("/api/cap-table/option-grants", { method: "POST", body: {
        shareholder_name: form.employee_name.trim(),
        instrument_name: "Option Pool",
        shares_granted,
        strike_price_dollars,
        grant_date: form.grant_date,
        cliff_months: parseInt(form.cliff_months, 10),
        vest_total_months: parseInt(form.vest_total_months, 10),
        vesting_schedule: form.vesting_schedule,
        memo: form.memo || null,
      }});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cap-table"] });
      toast.success("Option grant recorded");
      setForm(EMPTY_GRANT); onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const set = (k: keyof typeof EMPTY_GRANT) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Grant stock options</DialogTitle>
      <DialogContent>
        <div className="space-y-3 pt-2">
          <TextField label="Employee name" value={form.employee_name} onChange={set("employee_name")} fullWidth size="small" required placeholder="e.g. Alex Johnson" />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Shares" value={form.shares_str} onChange={set("shares_str")} size="small" required type="number" inputProps={{ min:1, step:"1000" }} placeholder="10000" />
            <MoneyField label="Strike price / share" currency="USD" value={form.strike_str} onChange={(v) => setForm(f => ({ ...f, strike_str: v }))} size="small" required placeholder="1.00" />
          </div>
          <TextField label="Grant date" value={form.grant_date} onChange={set("grant_date")} fullWidth size="small" required type="date" InputLabelProps={{ shrink: true }} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Cliff (months)" value={form.cliff_months} onChange={set("cliff_months")} size="small" type="number" inputProps={{ min:0, max:24 }} />
            <TextField label="Vest over (months)" value={form.vest_total_months} onChange={set("vest_total_months")} size="small" type="number" inputProps={{ min:12, max:72 }} />
          </div>
          <TextField select label="Vesting schedule" value={form.vesting_schedule} onChange={set("vesting_schedule")} fullWidth size="small">
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="annual">Annual</MenuItem>
          </TextField>
          <TextField label="Memo (optional)" value={form.memo} onChange={set("memo")} fullWidth size="small" multiline rows={2} placeholder="Seed grant, promotion, etc." />
        </div>
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-slate-50">Cancel</button>
        <button type="button" onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !form.employee_name.trim() || !form.shares_str || !form.strike_str}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50">
          {mutation.isPending ? "Saving…" : "Issue grant"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit Shareholder dialog ──────────────────────────────────────────────────

type EditShareholderForm = { name: string; email: string; shareholder_type: string; notes: string };

function EditShareholderDialog({ open, shareholder, onClose }: { open: boolean; shareholder: OwnershipRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<EditShareholderForm>({ name: "", email: "", shareholder_type: "other", notes: "" });

  // Sync form when shareholder changes
  useState(() => {
    if (shareholder) {
      setForm({ name: shareholder.shareholder_name, email: "", shareholder_type: shareholder.shareholder_type, notes: "" });
    }
  });

  // Reset form when dialog opens
  const handleOpen = (sh: OwnershipRow | null) => {
    if (sh) setForm({ name: sh.shareholder_name, email: "", shareholder_type: sh.shareholder_type, notes: "" });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!shareholder) return;
      const body: Record<string, string> = {};
      if (form.name.trim()) body.name = form.name.trim();
      if (form.email.trim()) body.email = form.email.trim();
      if (form.shareholder_type) body.shareholder_type = form.shareholder_type;
      if (form.notes.trim()) body.notes = form.notes.trim();
      return api(`/api/cap-table/shareholders/${shareholder.shareholder_id}`, { method: "PATCH", body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cap-table"] });
      toast.success("Saved");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const set = (k: keyof EditShareholderForm) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth TransitionProps={{ onEnter: () => handleOpen(shareholder) }}>
      <DialogTitle>Edit shareholder</DialogTitle>
      <DialogContent>
        <div className="space-y-3 pt-2">
          <TextField label="Name" value={form.name} onChange={set("name")} fullWidth size="small" />
          <TextField label="Email" value={form.email} onChange={set("email")} fullWidth size="small" type="email" placeholder="(leave blank to keep existing)" />
          <TextField select label="Type" value={form.shareholder_type} onChange={set("shareholder_type")} fullWidth size="small">
            {Object.entries(SHAREHOLDER_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
          <TextField label="Notes" value={form.notes} onChange={set("notes")} fullWidth size="small" multiline rows={2} />
        </div>
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-slate-50">Cancel</button>
        <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
          {mutation.isPending ? "Saving…" : "Save"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Transaction Correction dialog ────────────────────────────────────────────

function CorrectionDialog({ open, transaction, onClose }: { open: boolean; transaction: Transaction | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [newAmountStr, setNewAmountStr] = useState("");
  const [reason, setReason] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      if (!transaction) return;
      const new_amount_cents = Math.round(parseFloat(newAmountStr) * 100);
      if (isNaN(new_amount_cents) || new_amount_cents < 0) throw new Error("Enter a valid amount.");
      if (!reason.trim()) throw new Error("Correction reason is required.");
      return api(`/api/cap-table/transactions/${transaction.id}/correct`, {
        method: "POST",
        body: { new_amount_cents, correction_reason: reason.trim(), gl_cash_account_code: "1110" },
      });
    },
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["cap-table"] });
      const jNos = [
        r.reversal_journal_no ? `Reversal J-${r.reversal_journal_no}` : null,
        r.correction_journal_no ? `Correction J-${r.correction_journal_no}` : null,
      ].filter(Boolean).join(" · ");
      toast.success(`Corrected${jNos ? ` · ${jNos}` : ""}${r.warning ? ` — ${r.warning}` : ""}`);
      setNewAmountStr(""); setReason(""); onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Correct transaction</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          {transaction && (
            <div className="bg-slate-50 dark:bg-gray-800 rounded-lg px-4 py-3 text-sm space-y-1">
              <p className="font-medium text-slate-800 dark:text-slate-100">{transaction.shareholder_name} — {transaction.instrument_name}</p>
              <p className="text-slate-500">{transaction.transaction_date} · {usd(transaction.amount_cents)}{transaction.memo ? ` · ${transaction.memo}` : ""}</p>
            </div>
          )}
          <MoneyField label="New amount" currency="USD" value={newAmountStr} onChange={(v) => setNewAmountStr(v)}
            fullWidth size="small" required />
          <TextField label="Correction reason" value={reason} onChange={e => setReason(e.target.value)}
            fullWidth size="small" required multiline rows={2} placeholder="e.g. Wire amount was $500,000 not $250,000" />
          <p className="text-xs text-slate-400">
            This will create a reversal entry and a new corrected entry — both will appear in the GL and equity ledger.
          </p>
        </div>
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-slate-50">Cancel</button>
        <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending || !newAmountStr || !reason.trim()}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50">
          {mutation.isPending ? "Correcting…" : "Correct transaction"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit Grant dialog ────────────────────────────────────────────────────────

type EditGrantForm = { memo: string; cliff_months: string; vest_total_months: string; vesting_schedule: string };

function EditGrantDialog({ open, grant, onClose }: { open: boolean; grant: OptionGrant | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<EditGrantForm>({ memo: "", cliff_months: "12", vest_total_months: "48", vesting_schedule: "monthly" });

  const syncForm = (g: OptionGrant | null) => {
    if (g) setForm({ memo: g.memo ?? "", cliff_months: String(g.cliff_months), vest_total_months: String(g.vest_total_months), vesting_schedule: g.vesting_schedule ?? "monthly" });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!grant) return;
      return api(`/api/cap-table/option-grants/${grant.grant_id}`, {
        method: "PATCH",
        body: {
          memo: form.memo || null,
          cliff_months: parseInt(form.cliff_months, 10),
          vest_total_months: parseInt(form.vest_total_months, 10),
          vesting_schedule: form.vesting_schedule,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cap-table"] });
      toast.success("Saved");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const set = (k: keyof EditGrantForm) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth TransitionProps={{ onEnter: () => syncForm(grant) }}>
      <DialogTitle>Edit option grant</DialogTitle>
      <DialogContent>
        <div className="space-y-3 pt-2">
          {grant && <p className="text-sm text-slate-500">{grant.shareholder_name} · {grant.shares_granted.toLocaleString()} shares · {grant.grant_date}</p>}
          <TextField label="Memo" value={form.memo} onChange={set("memo")} fullWidth size="small" multiline rows={2} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Cliff (months)" value={form.cliff_months} onChange={set("cliff_months")} size="small" type="number" inputProps={{ min: 0, max: 24 }} />
            <TextField label="Vest over (months)" value={form.vest_total_months} onChange={set("vest_total_months")} size="small" type="number" inputProps={{ min: 1, max: 72 }} />
          </div>
          <TextField select label="Vesting schedule" value={form.vesting_schedule} onChange={set("vesting_schedule")} fullWidth size="small">
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="annual">Annual</MenuItem>
          </TextField>
        </div>
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-slate-50">Cancel</button>
        <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50">
          {mutation.isPending ? "Saving…" : "Save"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Shareholders tab ─────────────────────────────────────────────────────────

function ShareholdersTab({ onAddInvestment }: { onAddInvestment: () => void }) {
  const summaryQ = useQuery({ queryKey: ["cap-table","summary"], queryFn: () => api<{ summary: OwnershipRow[] }>("/api/cap-table/summary") });
  const txQ     = useQuery({ queryKey: ["cap-table","transactions"], queryFn: () => api<{ transactions: Transaction[] }>("/api/cap-table/transactions") });
  const instrQ  = useQuery({ queryKey: ["cap-table","instruments"], queryFn: () => api<{ instruments: Instrument[] }>("/api/cap-table/instruments") });

  const [editShareholder, setEditShareholder] = useState<OwnershipRow | null>(null);
  const [correctTxn, setCorrectTxn] = useState<Transaction | null>(null);

  const summary = summaryQ.data?.summary ?? [];
  const transactions = txQ.data?.transactions ?? [];
  const totalRaised = summary.reduce((s, r) => s + r.total_amount_cents, 0);
  const shareholderCount = new Set(summary.map(r => r.shareholder_id)).size;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total raised", value: usd(totalRaised) },
          { label: "Shareholders", value: String(shareholderCount) },
          { label: "Instruments", value: String(instrQ.data?.instruments.length ?? 0) },
        ].map(s => (
          <div key={s.label} className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 px-5 py-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Ownership bar */}
      {summary.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 px-5 py-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Ownership</p>
          <OwnershipBar rows={summary} />
        </div>
      )}

      {/* Shareholders table */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Shareholders</h2>
          <button type="button" onClick={onAddInvestment} className="text-xs text-blue-600 hover:underline">+ Record investment</button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 dark:border-gray-800">
              {["Name","Type","Instrument","Shares","Invested","Ownership",""].map((h, i) => (
                <th key={`sh-h-${i}`} className={`px-${i===0?5:4} py-2.5 font-medium text-slate-500 dark:text-slate-400 text-${i>=3&&i<=5?"right":"left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {summaryQ.isLoading ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : summary.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">No shareholders yet — record the first investment.</td></tr>
            ) : summary.map(row => (
              <tr key={`${row.shareholder_id}-${row.instrument_name}`} className="border-b border-slate-50 dark:border-gray-800/50 hover:bg-slate-50/50">
                <td className="px-5 py-3 font-medium text-slate-900 dark:text-slate-100">{row.shareholder_name}</td>
                <td className="px-4 py-3"><Badge label={SHAREHOLDER_LABELS[row.shareholder_type] ?? row.shareholder_type} color={SHAREHOLDER_COLORS[row.shareholder_type] ?? "bg-slate-100 text-slate-500"} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-700 dark:text-slate-300">{row.instrument_name}</span>
                    <Badge label={INSTRUMENT_LABELS[row.instrument_type] ?? row.instrument_type} color={INSTRUMENT_COLORS[row.instrument_type] ?? "bg-slate-100 text-slate-500"} />
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-slate-600 font-mono text-xs">{row.total_shares > 0 ? row.total_shares.toLocaleString() : "—"}</td>
                <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">{usd(row.total_amount_cents)}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100">{row.ownership_pct.toFixed(1)}%</td>
                <td className="px-4 py-3">
                  <button type="button" onClick={() => setEditShareholder(row)} className="text-slate-400 hover:text-slate-600" title="Edit shareholder">✏️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EditShareholderDialog open={editShareholder !== null} shareholder={editShareholder} onClose={() => setEditShareholder(null)} />
      <CorrectionDialog open={correctTxn !== null} transaction={correctTxn} onClose={() => setCorrectTxn(null)} />

      {/* Transaction history */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Transaction History</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 dark:border-gray-800">
              {["Date","Investor","Instrument","Type","Amount","GL / Memo",""].map((h,i) => (
                <th key={`tx-h-${i}`} className={`px-${i===0?5:4} py-2.5 font-medium text-slate-500 dark:text-slate-400 text-${i===4?"right":"left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {txQ.isLoading ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : transactions.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400">No transactions yet.</td></tr>
            ) : transactions.map(t => (
              <tr key={t.id} className="border-b border-slate-50 dark:border-gray-800/50 hover:bg-slate-50/50">
                <td className="px-5 py-3 text-slate-500 tabular-nums whitespace-nowrap">{t.transaction_date}</td>
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{t.shareholder_name}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-700">{t.instrument_name}</span>
                    <Badge label={INSTRUMENT_LABELS[t.instrument_type] ?? t.instrument_type} color={INSTRUMENT_COLORS[t.instrument_type] ?? "bg-slate-100 text-slate-500"} />
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{TXN_LABELS[t.transaction_type] ?? t.transaction_type}</td>
                <td className="px-4 py-3 text-right font-medium text-slate-900 dark:text-slate-100">{usd(t.amount_cents)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 text-slate-500 text-xs">
                    {t.journal_id && <a href={`/journals/${t.journal_id}`} className="text-blue-600 hover:underline font-medium whitespace-nowrap">GL →</a>}
                    {t.memo && <span className="truncate max-w-[180px]" title={t.memo}>{t.memo}</span>}
                    {!t.journal_id && !t.memo && "—"}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button type="button" onClick={() => setCorrectTxn(t)} className="text-xs text-amber-600 hover:underline whitespace-nowrap">Correct</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Send for Signature dialog ────────────────────────────────────────────────

const EMPTY_SIGN = { officer_name: "", officer_email: "", mode: "template" as "template" | "upload" };

function SendForSignatureDialog({ open, grant, onClose }: { open: boolean; grant: OptionGrant | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_SIGN);
  const [file, setFile] = useState<File | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!grant) return;
      if (!form.officer_name.trim() || !form.officer_email.trim()) throw new Error("Officer name and email are required.");
      if (form.mode === "upload" && !file) throw new Error("Please select a PDF to upload.");
      const fd = new FormData();
      fd.append("officer_name", form.officer_name.trim());
      fd.append("officer_email", form.officer_email.trim());
      if (form.mode === "upload" && file) {
        fd.append("document", file);
        fd.append("use_template", "false");
      } else {
        fd.append("use_template", "true");
      }
      // Use raw fetch for multipart — api() helper sends JSON
      const token = getToken();
      const res = await fetch(`/api/cap-table/option-grants/${grant.grant_id}/send-for-signature`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail ?? "Failed to send for signature.");
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["cap-table", "option-grants"] });
      if (data?.status === "pdf_ready") {
        toast.success("PDF generated — configure BYNAUS_CORE_URL to enable e-signing.");
      } else {
        toast.success("Sent for signature — both parties will receive an email.");
      }
      setForm(EMPTY_SIGN);
      setFile(null);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const set = (k: keyof typeof EMPTY_SIGN) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send option grant for signature</DialogTitle>
      <DialogContent>
        {grant && (
          <div className="space-y-4 pt-2">
            <div className="bg-slate-50 dark:bg-gray-800 rounded-lg px-4 py-3 text-sm">
              <p className="font-medium text-slate-800 dark:text-slate-100">{grant.shareholder_name}</p>
              <p className="text-slate-500 mt-0.5">
                {grant.shares_granted.toLocaleString()} options · {usdExact(grant.strike_price_cents)}/share · {grant.grant_date}
              </p>
            </div>

            {/* Document mode */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Grant agreement document</p>
              <div className="flex gap-3">
                <label className={`flex-1 border rounded-lg px-4 py-3 cursor-pointer transition-colors ${form.mode === "template" ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="radio" name="mode" value="template" checked={form.mode === "template"} onChange={() => setForm(f => ({ ...f, mode: "template" }))} className="sr-only" />
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Use sample template</p>
                  <p className="text-xs text-slate-500 mt-0.5">System generates a standard grant notice from grant data</p>
                </label>
                <label className={`flex-1 border rounded-lg px-4 py-3 cursor-pointer transition-colors ${form.mode === "upload" ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="radio" name="mode" value="upload" checked={form.mode === "upload"} onChange={() => setForm(f => ({ ...f, mode: "upload" }))} className="sr-only" />
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Upload lawyer's PDF</p>
                  <p className="text-xs text-slate-500 mt-0.5">Use the agreement provided by your counsel</p>
                </label>
              </div>
              {form.mode === "upload" && (
                <div className="mt-3">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={e => setFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                  />
                  {file && <p className="text-xs text-slate-400 mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
                </div>
              )}
            </div>

            {/* Officer */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Company signatory (signs first)</p>
              <div className="space-y-2">
                <TextField label="Officer name" value={form.officer_name} onChange={set("officer_name")} fullWidth size="small" required placeholder="e.g. Dan Cornish, CEO" />
                <TextField label="Officer email" value={form.officer_email} onChange={set("officer_email")} fullWidth size="small" required type="email" placeholder="dan@company.com" />
              </div>
            </div>

            <p className="text-xs text-slate-400">
              The officer signs first, then {grant.shareholder_name} receives an email with a signing link.
            </p>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-slate-50">Cancel</button>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !form.officer_name.trim() || !form.officer_email.trim() || (form.mode === "upload" && !file)}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
        >
          {mutation.isPending ? "Sending…" : "Send for signature"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Options tab ──────────────────────────────────────────────────────────────

function OptionsTab({ onGrantOptions }: { onGrantOptions: () => void }) {
  const grantsQ = useQuery({ queryKey: ["cap-table","option-grants"], queryFn: () => api<{ grants: OptionGrant[] }>("/api/cap-table/option-grants") });
  const poolQ   = useQuery({ queryKey: ["cap-table","option-pool"],   queryFn: () => api<OptionPool>("/api/cap-table/option-pool") });
  const [signingGrant, setSigningGrant] = useState<OptionGrant | null>(null);
  const [editGrant, setEditGrant] = useState<OptionGrant | null>(null);

  const grants = grantsQ.data?.grants ?? [];
  const pool = poolQ.data;

  const totalGranted  = grants.reduce((s, g) => s + g.shares_granted, 0);
  const totalVested   = grants.reduce((s, g) => s + g.shares_vested, 0);
  const totalExercised = grants.reduce((s, g) => s + g.shares_exercised, 0);

  return (
    <div className="space-y-6">
      {/* Pool stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Pool authorized",   value: pool ? pool.total_authorized.toLocaleString() : "—", sub: "shares" },
          { label: "Granted",           value: totalGranted.toLocaleString(),   sub: "shares" },
          { label: "Vested",            value: totalVested.toLocaleString(),     sub: "shares" },
          { label: "Exercised",         value: totalExercised.toLocaleString(), sub: "shares" },
        ].map(s => (
          <div key={s.label} className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 px-5 py-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1 tabular-nums">{s.value}</p>
            <p className="text-xs text-slate-400 mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Pool utilisation bar */}
      {pool && pool.total_authorized > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pool utilisation</p>
            <p className="text-xs text-slate-500">
              {totalGranted.toLocaleString()} / {pool.total_authorized.toLocaleString()} shares granted
              · {pool.total_available.toLocaleString()} available
            </p>
          </div>
          <div className="h-3 rounded-full bg-slate-100 dark:bg-gray-700 overflow-hidden">
            <div className="h-full rounded-full bg-purple-500" style={{ width: `${Math.min(100, (totalGranted / pool.total_authorized) * 100)}%` }} />
          </div>
        </div>
      )}

      {/* Grants table */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Option Grants</h2>
          <button type="button" onClick={onGrantOptions} className="text-xs text-purple-600 hover:underline font-medium">+ Issue grant</button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 dark:border-gray-800">
              {["Employee","Grant date","Shares","Strike","Vesting","Status","Signature",""].map((h,i) => (
                <th key={`${h}${i}`} className={`px-${i===0?5:4} py-2.5 font-medium text-slate-500 text-left`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grantsQ.isLoading ? (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : grants.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-12 text-center">
                  <p className="text-slate-400 text-sm">No option grants yet.</p>
                  <button type="button" onClick={onGrantOptions} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white">
                    + Issue first grant
                  </button>
                </td>
              </tr>
            ) : grants.map(g => (
              <tr key={g.grant_id} className="border-b border-slate-50 dark:border-gray-800/50 hover:bg-slate-50/50">
                <td className="px-5 py-3 font-medium text-slate-900 dark:text-slate-100">{g.shareholder_name}</td>
                <td className="px-4 py-3 text-slate-500 tabular-nums whitespace-nowrap">{g.grant_date}</td>
                <td className="px-4 py-3">
                  <div>
                    <p className="font-medium text-slate-900 tabular-nums">{g.shares_granted.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">{g.shares_exercised > 0 ? `${g.shares_exercised.toLocaleString()} exercised` : "none exercised"}</p>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700 dark:text-slate-300 tabular-nums">{usdExact(g.strike_price_cents)}</td>
                <td className="px-4 py-3">
                  <VestingBar grant={g} />
                  <p className="text-xs text-slate-400 mt-0.5">{g.cliff_months}mo cliff · {g.vest_total_months}mo total</p>
                </td>
                <td className="px-4 py-3">
                  <Badge label={g.status} color={STATUS_COLORS[g.status] ?? "bg-slate-100 text-slate-500"} />
                </td>
                <td className="px-4 py-3">
                  {g.signing_status ? (
                    <Badge label={SIGNING_LABELS[g.signing_status] ?? g.signing_status} color={SIGNING_COLORS[g.signing_status] ?? "bg-slate-100 text-slate-500"} />
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {(!g.signing_status || g.signing_status === "declined" || g.signing_status === "voided") && (
                      <button
                        type="button"
                        onClick={() => setSigningGrant(g)}
                        className="text-xs text-blue-600 hover:underline whitespace-nowrap font-medium"
                      >
                        Send for signature →
                      </button>
                    )}
                    {g.signing_status === "completed" && (
                      <span className="text-xs text-green-600 font-medium">✓ Signed</span>
                    )}
                    {(g.signing_status === "pending" || g.signing_status === "in_progress") && (
                      <span className="text-xs text-amber-600">Awaiting…</span>
                    )}
                    <button type="button" onClick={() => setEditGrant(g)} className="text-slate-400 hover:text-slate-600" title="Edit grant">✏️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SendForSignatureDialog
        open={signingGrant !== null}
        grant={signingGrant}
        onClose={() => setSigningGrant(null)}
      />
      <EditGrantDialog open={editGrant !== null} grant={editGrant} onClose={() => setEditGrant(null)} />
    </div>
  );
}

// ─── Equity Ledger tab ────────────────────────────────────────────────────────

function LedgerTab() {
  const ledgerQ  = useQuery({ queryKey: ["cap-table","equity-ledger"],  queryFn: () => api<{ entries: EquityLedgerEntry[] }>("/api/cap-table/equity-ledger") });
  const summaryQ = useQuery({ queryKey: ["cap-table","equity-summary"], queryFn: () => api<EquitySummary>("/api/cap-table/equity-summary") });

  const entries = ledgerQ.data?.entries ?? [];
  const summary = summaryQ.data;
  const totals  = summary?.totals;

  return (
    <div className="space-y-6">
      {/* Totals — 4 cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Authorized",     value: totals?.authorized        ?? 0, color: "text-slate-800 dark:text-slate-100" },
          { label: "Issued",         value: totals?.issued            ?? 0, color: "text-emerald-700 dark:text-emerald-400" },
          { label: "Options out.",   value: totals?.options_outstanding ?? 0, color: "text-purple-700 dark:text-purple-400" },
          { label: "Fully diluted",  value: totals?.fully_diluted     ?? 0, color: "text-blue-700 dark:text-blue-400" },
        ].map(s => (
          <div key={s.label} className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 px-5 py-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 tabular-nums ${s.color}`}>{s.value.toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-0.5">shares</p>
          </div>
        ))}
      </div>

      {/* Per-instrument breakdown */}
      {summary && summary.by_instrument.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">By Instrument</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 dark:border-gray-800">
                {["Instrument","Type","Authorized","Issued","Outstanding","Options","Fully Diluted"].map((h, i) => (
                  <th key={h} className={`px-${i===0?5:4} py-2.5 font-medium text-slate-500 text-${i<=1?"left":"right"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary.by_instrument.map(row => (
                <tr key={row.instrument_id} className="border-b border-slate-50 dark:border-gray-800/50 hover:bg-slate-50/50">
                  <td className="px-5 py-3 font-medium text-slate-900 dark:text-slate-100">{row.instrument_name}</td>
                  <td className="px-4 py-3">
                    <Badge label={INSTRUMENT_LABELS[row.instrument_type] ?? row.instrument_type} color={INSTRUMENT_COLORS[row.instrument_type] ?? "bg-slate-100 text-slate-500"} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">{row.authorized.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">{row.issued.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900 dark:text-slate-100">{row.outstanding.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-purple-700 dark:text-purple-400">
                    {row.options_granted > 0 ? row.options_granted.toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-blue-700 dark:text-blue-400">{row.fully_diluted.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Ledger entries */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Share Movement Ledger</h2>
          <p className="text-xs text-slate-400 mt-0.5">Append-only · auto-posted from investments and grants · most recent first</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 dark:border-gray-800">
              {["Date","Event","Instrument","From","To","Shares","Balance","Memo"].map((h, i) => (
                <th key={h} className={`px-${i===0||i===7?5:4} py-2.5 font-medium text-slate-500 text-${i>=5&&i<=6?"right":"left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ledgerQ.isLoading ? (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-12 text-center">
                  <p className="text-slate-400 text-sm">No ledger entries yet.</p>
                  <p className="text-xs text-slate-300 mt-1">Entries are posted automatically when you record investments and option grants.</p>
                </td>
              </tr>
            ) : entries.map(e => (
              <tr key={e.id} className="border-b border-slate-50 dark:border-gray-800/50 hover:bg-slate-50/50">
                <td className="px-5 py-3 text-slate-500 tabular-nums whitespace-nowrap">{e.entry_date}</td>
                <td className="px-4 py-3">
                  <Badge label={EVENT_LABELS[e.event_type] ?? e.event_type} color={EVENT_COLORS[e.event_type] ?? "bg-slate-100 text-slate-500"} />
                </td>
                <td className="px-4 py-3 text-slate-700 dark:text-slate-300 whitespace-nowrap">{e.instrument_name ?? "—"}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{e.from_party_name ?? "—"}</td>
                <td className="px-4 py-3 text-slate-700 dark:text-slate-300 text-xs font-medium">{e.to_party_name ?? "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900 dark:text-slate-100">
                  {e.shares.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                  {e.running_shares.toLocaleString()}
                </td>
                <td className="px-5 py-3 text-slate-400 text-xs max-w-[180px] truncate" title={e.memo ?? ""}>{e.memo ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = "shareholders" | "options" | "ledger";

function CapTablePage() {
  const [tab, setTab] = useState<Tab>("shareholders");
  const [investmentOpen, setInvestmentOpen] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const instrQ = useQuery({ queryKey: ["cap-table","instruments"], queryFn: () => api<{ instruments: Instrument[] }>("/api/cap-table/instruments") });

  const tabCls = (t: Tab) =>
    `px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
      tab === t
        ? "border-blue-600 text-blue-600 dark:text-blue-400"
        : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
    }`;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Cap Table</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Shareholders, option pool, and equity ledger</p>
          </div>
          <div className="flex items-center gap-2">
            {tab === "shareholders" && (
              <button type="button" onClick={() => setInvestmentOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5">
                + Record investment
              </button>
            )}
            {tab === "options" && (
              <button type="button" onClick={() => setGrantOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white rounded-md px-3 py-1.5">
                + Issue grant
              </button>
            )}
          </div>
        </div>
        {/* Tabs */}
        <div className="flex gap-1 mt-3 border-b border-slate-100 dark:border-gray-800 -mb-4">
          <button type="button" className={tabCls("shareholders")} onClick={() => setTab("shareholders")}>Shareholders</button>
          <button type="button" className={tabCls("options")} onClick={() => setTab("options")}>Options</button>
          <button type="button" className={tabCls("ledger")} onClick={() => setTab("ledger")}>Equity Ledger</button>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 px-6 py-6 bg-slate-50 dark:bg-gray-950">
        {tab === "shareholders" && <ShareholdersTab onAddInvestment={() => setInvestmentOpen(true)} />}
        {tab === "options"      && <OptionsTab onGrantOptions={() => setGrantOpen(true)} />}
        {tab === "ledger"       && <LedgerTab />}
      </div>

      <AddInvestmentDialog open={investmentOpen} onClose={() => setInvestmentOpen(false)} instruments={instrQ.data?.instruments ?? []} />
      <GrantOptionsDialog  open={grantOpen}      onClose={() => setGrantOpen(false)} />
    </div>
  );
}
